import { useEffect, useReducer, useState } from 'react'
import { LicenseAndPaymentData } from '@/api/types/licenseAndPayment.types'
import { getNextLicensePaymentCheckTime } from '@/utils/licensePaymentStatus'

const REMINDER_INTERVAL = 60 * 60 * 1000

interface Reminder {
  key: string | null
  expirationDate: string
  until: number
}

const readReminder = (key: string | null): Reminder | null | undefined => {
  if (!key) return null
  try {
    const value = JSON.parse(localStorage.getItem(key) || 'null')
    return value &&
      typeof value.expirationDate === 'string' &&
      Number.isFinite(value.until)
      ? { key, expirationDate: value.expirationDate, until: value.until }
      : null
  } catch {
    return undefined
  }
}

export const useLicensePaymentReminder = (
  data: LicenseAndPaymentData | undefined,
  userId: number | string | undefined,
  canManagePayments: boolean,
) => {
  // localStorage is scoped to the platform origin; each administrator has their own delay.
  const key =
    canManagePayments && userId != null
      ? `license-payment-reminder:${userId}`
      : null
  const [reminder, setReminder] = useState(() => readReminder(key) ?? null)
  const [refreshKey, refresh] = useReducer((value: number) => value + 1, 0)
  const now = Date.now()
  const isSnoozed = Boolean(
    reminder &&
      reminder.key === key &&
      reminder.expirationDate === data?.expiration_date &&
      reminder.until > now &&
      reminder.until <= now + REMINDER_INTERVAL,
  )

  useEffect(() => {
    const reloadReminder = () => {
      const stored = readReminder(key)
      if (stored !== undefined) setReminder(stored)
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key === key || event.key === null) reloadReminder()
    }
    const onFocus = () => {
      reloadReminder()
      refresh()
    }
    reloadReminder()
    window.addEventListener('storage', onStorage)
    window.addEventListener('focus', onFocus)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('focus', onFocus)
    }
  }, [key])

  useEffect(() => {
    const nextCheck = getNextLicensePaymentCheckTime(data, now)
    const deadlines = [nextCheck, isSnoozed ? reminder?.until : null].filter(
      (time): time is number => typeof time === 'number' && time > now,
    )
    if (!deadlines.length) return
    const timer = window.setTimeout(
      refresh,
      Math.min(Math.min(...deadlines) - now, 2147483647),
    )
    return () => window.clearTimeout(timer)
  }, [data, now, isSnoozed, reminder])

  const remindLater = () => {
    if (!canManagePayments || !data?.expiration_date) return
    const value = {
      key,
      expirationDate: data.expiration_date,
      until: Date.now() + REMINDER_INTERVAL,
    }
    setReminder(value)
    if (key) {
      try {
        localStorage.setItem(key, JSON.stringify(value))
      } catch {
        // The current tab can still postpone a reminder when browser storage is unavailable.
      }
    }
  }

  return { now, isSnoozed, remindLater, refreshKey }
}
